
// SignageViewOfficeProduct.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import SharedEditProductUI from "components/SignageOfficeAgentInventoryProduct";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { getAuthCookie } from "../../utils/authCookies";
type SelectOption = { value: string; label: string };
type PreviewFile = { file: File; url: string };
type UploadedImage = { alt_text: string; url: string };

type ViewRow = {
  warehouse_name: string;
  quantity: number;
  zone_name: string;
  rack_name: string;
  bin_name: string;
};

const conditionOptions: SelectOption[] = [
  { value: "new", label: "New" },
  { value: "used_like_new", label: "Used - Like New" },
  { value: "used_good", label: "Used - Good" },
];

const statusOptions: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const SignageViewOfficeProduct: React.FC = () => {
  const { officeId } = useParams<{ officeId: string }>();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const token = getAuthCookie("sessionToken") || "";
  const [loading, setLoading] = useState(true);

  // Full office inventory object from API
  const [officeProduct, setOfficeProduct] = useState<any>(null);

  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);

  // Read-only warehouse rows (your old table)
  const [viewRows, setViewRows] = useState<ViewRow[]>([]);

  // ── Fetch office product (VIEW) ───────────────────────────────────────
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch product data");
        const data = await res.json();
        setOfficeProduct(data);
        setUploadedFileMeta(data?.product?.variants?.[0]?.images || []);
        // // Map product images (if any) to shared UI format
        // const imgs = Array.isArray(data?.product?.variants?.[0]?.images)
        //   ? data.product.images.map((img: any) => ({
        //     alt_text: img?.alt_text ?? "",
        //     url: img?.url ?? "",
        //   }))
        //   : [];
        //   console.log("imgs",imgs)
        // setUploadedFiles(imgs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    if (officeId && productId) fetchProduct();
  }, [officeId, productId, token]);

  // ── Build read-only rows for your old warehouse table ────────────────
  useEffect(() => {
    // Using the "group.location[]" structure like Agent View
    if (!officeProduct?.linked_installer_details?.storage_locations) return;

    const rows: ViewRow[] =
      officeProduct.linked_installer_details.storage_locations?.flatMap(
        (group: any) =>
          (group.location || []).map((loc: any) => ({
            warehouse_name: loc?.warehouse_name || loc?.warehouse || "",
            quantity: loc?.quantity ?? group?.quantity ?? 0,
            zone_name: loc?.zone_name || loc?.zone || "",
            rack_name: loc?.rack_name || loc?.rack || "",
            bin_name: loc?.bin_name || loc?.bin || "",
          }))
      ) || [];

    setViewRows(rows);
  }, [officeProduct]);

  // ── Single-option lists so selects show current values (readOnly) ────
  const brandOptionsMemo: SelectOption[] = useMemo(() => {
    const p = officeProduct?.product;
    const val = p?.brand_id ?? "";
    const label = p?.brand ?? "";
    return val ? [{ value: String(val), label: String(label) }] : [];
  }, [officeProduct]);

  const categoryOptionsMemo: SelectOption[] = useMemo(() => {
    const p = officeProduct?.product;
    const val = p?.category_id ?? "";
    const label = p?.category ?? "";
    return val ? [{ value: String(val), label: String(label) }] : [];
  }, [officeProduct]);

  // If you have a single product object in edit screen:
  const dataForMap = React.useMemo(() => (officeProduct ? officeProduct : []), [officeProduct]);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // ── Minimal Formik-like adapter for the shared UI (READ-ONLY) ────────
  const formik = useMemo(() => {
    const p = officeProduct?.product || {};

    return {
      values: {
        name: p?.name ?? "",
        sku: p?.sku ?? "",
        description: p?.description ?? "",
        
        // flat numeric fields used by Shared UI
        qty: p?.variants?.[0]?.inventory?.[0]?.available ?? 0,
        threshold_qty: p?.threshold_qty ?? 0,

        // dimensions flattened from package_dimensions
        length: p?.package_dimensions?.length ?? "",
        height: p?.package_dimensions?.height ?? "",
        width: p?.package_dimensions?.width ?? "",
        weight: p?.package_dimensions?.weight ?? "",

        item_condition: p?.item_condition ?? "new",

        // IMPORTANT: Shared UI expects "statuss"
        statuss: p?.status ?? "Active",

        brand: p?.brand_id ?? "",
        category: p?.category_id ?? "",
      },
      handleChange: () => { },
      handleBlur: () => { },
      setFieldValue: () => { },
      errors: {},
      touched: {},
    };
  }, [officeProduct]);

  // Read-only view - no file operations allowed (no-op handlers)
  const handleAddFiles = () => { };
  const handleRemovePreview = () => { };
  const handleRemoveUploaded = () => { };

  if (loading) return <div className="p-4">Loading…</div>;
  if (!officeProduct?.product)
    return <div className="p-4 text-danger">Product not found.</div>;

  return (
    <div className="content">
      <SharedEditProductUI
        title="View Product"
        formik={formik}
        brandOptions={brandOptionsMemo}
        categoryOptions={categoryOptionsMemo}
        conditionOptions={conditionOptions}
        statusOptions={statusOptions}
        saving={false}
        onBack={() => navigate(-1)}
        onSubmit={() => { }}
        previewFiles={[]}
        uploadedFiles={uploadedFileMeta}
        onAddFiles={handleAddFiles}
        onRemovePreview={handleRemovePreview}
        onRemoveUploaded={handleRemoveUploaded}
        baseUrl={process.env.REACT_APP_BASE_URL}
        readOnly
        hideWarehouseTable
        hideSaveButton
        resolveSrc={(url) =>
          getImageSrc(url, imageUrlMap) ||
          (String(url).startsWith("http") ? String(url)
            : `${process.env.REACT_APP_BASE_URL}/${String(url || "").replace(
              /^https?:\/\/[^/]+\/(uploads\/.*)/, '$1')}`)
        }
      />
    </div>
  );
};

export default SignageViewOfficeProduct;